import graphqlHelperService from "./graphqlHelper.service";
import { SampleExam, SampleExamAdminInput, SampleExamFilledInput, ExamResult, SampleExamAdmin } from "../types/sampleExam";
import { SampleExamLog } from "../types/sampleExamLog";
import { Answer } from "../types/question";

function shuffleArray(array: Answer[]) {
    for (var i = array.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var temp = array[i];
        array[i] = array[j];
        array[j] = temp;
    }
    return array;
}

export const sampleExamService = {
    sampleExam: async (sampleExamId: number, projectId: number): Promise<SampleExam> => {
        const apolloResult = await graphqlHelperService.sendQuery(
            sampleExamQuery(sampleExamId, projectId)
        );
        const exam = apolloResult.sampleExam as SampleExam;
        exam.questions.forEach(question => {
            question.possibleAnswers = shuffleArray(question.possibleAnswers);
        });

        return exam;
    },
    sampleExamsByCategoryId: async (categoryId: number): Promise<SampleExam[]> => {
        const apolloResult = await graphqlHelperService.sendQuery(
            sampleExamsByCategoryIdQuery(categoryId)
        );
        return apolloResult.sampleExamsByCategoryId as SampleExam[];
    },
    sampleExamLogs: async (): Promise<SampleExamLog[]> => {
        const apolloResult = await graphqlHelperService.sendQuery(
            sampleExamLogsQuery()
        );
        return apolloResult.sampleExamLogs as SampleExamLog[];
    },
    createSampleExam: async (sampleExam: SampleExamAdminInput): Promise<number> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            createSampleExamQuery(sampleExam)
        );
        return apolloResult.createSampleExam as number;
    },
    updateSampleExam: async (sampleExam: SampleExamAdmin): Promise<boolean> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            updateSampleExamQuery(sampleExam)
        );
        return apolloResult.updateSampleExam as boolean;
    },
    deleteSampleExam: async (sampleExamId: number): Promise<boolean> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            deleteSampleExamQuery(sampleExamId)
        );
        return apolloResult.deleteSampleExam as boolean;
    },
    checkSampleExam: async (sampleExam: SampleExamFilledInput): Promise<ExamResult> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            checkSampleExamQuery(sampleExam)
        );
        return apolloResult.checkSampleExam as ExamResult;
    },
    exportSampleExamLogs: async (filters?: {
        cursistNr?: number;
        locationCode?: string;
    }): Promise<string> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            exportSampleExamLogsQuery(filters)
        );
        return apolloResult.exportSampleExamLogs as string;
    },
}

const sampleExamQuery = (sampleExamId: number, projectId: number) => `query{
    sampleExam(sampleExamId: ${sampleExamId}, projectId: ${projectId}) {
        sampleExamId
        curinfoId
        categoryId
        duration
        amountOfQuestions
        amountCorrect
        questions {
            questionId
            categoryId
            text
            possibleAnswers {
                answerId
                questionId
                text
                correct
            }
            active
        }
    }
}`;

const sampleExamsByCategoryIdQuery = (categoryId: number) => `query{
    sampleExamsByCategoryId(categoryId: ${categoryId}) {
        sampleExamId
        categoryId
        duration
        amountOfQuestions
        amountCorrect
    }
}`;

const sampleExamLogsQuery = () => `query {
    sampleExamLogs{
        sampleExamLogId
        sampleExamId
        cursistNr
        entryDate
        amountCorrect
        amountOfQuestions
        grade
    }
}`;

const createSampleExamQuery = (sampleExam: SampleExamAdminInput) => `mutation{
    createSampleExam(sampleExam: {
        categoryId: ${sampleExam.categoryId},
        duration: ${sampleExam.duration},
        amountOfQuestions: ${sampleExam.amountOfQuestions},
        amountCorrect: ${sampleExam.amountCorrect}
    })
}`;

const updateSampleExamQuery = (sampleExam: SampleExamAdmin) => `mutation{
    updateSampleExam(sampleExam: {
        sampleExamId: ${sampleExam.sampleExamId},
        categoryId: ${sampleExam.categoryId},
        duration: ${sampleExam.duration},
        amountOfQuestions: ${sampleExam.amountOfQuestions},
        amountCorrect: ${sampleExam.amountCorrect}
    })
}`;

const deleteSampleExamQuery = (sampleExamId: number) => `mutation{
    deleteSampleExam(sampleExamId: ${sampleExamId})
}`;

const checkSampleExamQuery = (sampleExam: SampleExamFilledInput) => `mutation{
    checkSampleExam(sampleExam: {
        sampleExamId: ${sampleExam.sampleExamId},
        curinfoId: "${sampleExam.curinfoId}",
        questions: [
            ${sampleExam.questions && sampleExam.questions.map((question) => `
            {
                questionId: ${question.questionId},
                ${question.givenAnswerId ? `givenAnswerId: ${question.givenAnswerId},` : ``}
             }
        `)}
        ]
    }){
        passed
        grade
        questions {
            questionId
            givenAnswer
            text
            correctAnswers
            correct
        }
    }
}`;

const exportSampleExamLogsQuery = (filters?: {
    cursistNr?: number;
    locationCode?: string;
}) => {
    const filtersStr = filters ? `filters: {
        ${filters.cursistNr ? `cursistNr: ${filters.cursistNr}` : ''}
        ${filters.locationCode ? `locationCode: "${filters.locationCode}"` : ''}
    }` : '';

    return `mutation {
        exportSampleExamLogs${filtersStr ? `(${filtersStr})` : ''}
    }`;
}